/**
 * ScheduleEditorDialog — ONE converged form (SCHEDULER.md §11.1a, post-merge).
 *
 * No modes. A schedule is two orthogonal choices:
 *   - WHEN it starts — "When" presets: **Now** (default; saved PAUSED — the user
 *     presses the power button to launch it) or a cadence
 *     (Hourly/Daily/Weekdays/Weekly/Monthly/Once/Custom = a clock).
 *   - WHEN it stops  — an optional **Objective**: leave it blank → one pass;
 *     fill it → loop until the result meets the objective (rating + confidence).
 * Always: a prompt LIST (typed rows or uploaded + split on a separator) and a
 * Max-turns safety cap. The backend derives the mode from these fields.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Upload, X } from 'lucide-react';
import {
  PRESETS, WEEKDAYS, buildCadence, firesPerMonth, browserTimezone,
} from '@stepflow/ui-shared/utils/scheduleCadence.js';
import {
  splitPrompts, buildLoopBody, validateLoop, ratingOptions, DEFAULT_SEPARATOR, DEFAULT_MAX_TURNS,
} from '@stepflow/ui-shared/utils/scheduleMode.js';

const CHIP = 'rounded-full px-3 py-1 text-xs font-medium transition-colors';

// Short blurb per "When" option — shown in the left rail under each label.
const PRESET_DESC = {
  Now: 'Saved paused — press the power button to start it.',
  Hourly: 'Every hour.',
  Daily: 'Once a day at a set time.',
  Weekdays: 'Mon–Fri at a set time.',
  Weekly: 'Chosen days each week.',
  Monthly: 'A day each month.',
  Once: 'A single future date & time.',
  Custom: 'Your own cron expression.',
};

export default function ScheduleEditorDialog({ open, initial, onSave, onClose }) {
  const isEdit = Boolean(initial);
  // WHEN-it-starts
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('Now');
  const [time, setTime] = useState('09:00');
  const [weekdays, setWeekdays] = useState(['MON']);
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [at, setAt] = useState('');
  const [cron, setCron] = useState('');
  // Prompt list + stop gate
  const [prompts, setPrompts] = useState(['']);
  const [separator, setSeparator] = useState(DEFAULT_SEPARATOR);
  const [objective, setObjective] = useState('');
  const [rating, setRating] = useState('8.0');
  const [confidence, setConfidence] = useState('0.95');
  const [maxTurns, setMaxTurns] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    if (initial) {
      setName(initial.name || '');
      setPrompts(initial.prompts && initial.prompts.length
        ? initial.prompts
        : (initial.prompt ? [initial.prompt] : ['']));
      setSeparator(initial.separator || DEFAULT_SEPARATOR);
      const g = initial.gate || {};
      setObjective(g.objective || '');
      setRating(g.rating != null ? String(g.rating) : '8.0');
      setConfidence(g.confidence != null ? String(g.confidence) : '0.95');
      setMaxTurns(String(initial.guards?.maxTurns || 1));
      // Derive the "When" from an existing cadence (else Now).
      const c = initial.cadence || {};
      const type = String(c.type || '').toUpperCase();
      if (!type || !c.expr) setPreset('Now');
      else if (type === 'RATE') setPreset('Hourly');
      else if (type === 'ONCE') {
        setPreset('Once');
        const mm = /^at\((.+)\)$/.exec(c.expr || '');
        setAt(mm ? mm[1].slice(0, 16) : '');
      } else {
        setPreset('Custom');
        setCron(String(c.expr || '').replace(/^cron\(|\)$/g, ''));
      }
    } else {
      setName('');
      setPreset('Now'); setTime('09:00'); setWeekdays(['MON']);
      setDayOfMonth('1'); setAt(''); setCron('');
      setPrompts(['']); setSeparator(DEFAULT_SEPARATOR);
      setObjective(''); setRating('8.0'); setConfidence('0.95');
      setMaxTurns('1');
    }
  }, [open, initial]);

  // Adding an objective gives the loop room to converge: bump a default-1 cap
  // to 10 (don't override a value the user deliberately set higher).
  const prevHadObjective = useRef(false);
  useEffect(() => {
    const has = objective.trim().length > 0;
    if (has && !prevHadObjective.current && Number(maxTurns) <= 1) {
      setMaxTurns(String(DEFAULT_MAX_TURNS));
    }
    prevHadObjective.current = has;
  }, [objective, maxTurns]);

  const isNow = preset === 'Now';
  const cadence = useMemo(
    () => (isNow ? null : buildCadence({ preset, time, weekdays, dayOfMonth, at, cron, timezone: browserTimezone() })),
    [isNow, preset, time, weekdays, dayOfMonth, at, cron],
  );
  const estFires = useMemo(
    () => (cadence ? firesPerMonth({ ...cadence, expr: buildExpr(cadence) }) : 0),
    [cadence],
  );
  const cleanPrompts = useMemo(() => prompts.map((p) => p.trim()).filter(Boolean), [prompts]);
  const hasObjective = objective.trim().length > 0;

  // Loop validation only enforces the gate when an objective is given.
  const loopErr = validateLoop({
    mode: hasObjective ? 'REPEAT_UNTIL' : 'ONCE',
    prompts: cleanPrompts, objective, rating, confidence, maxTurns,
  });
  const nameOk = name.trim().length > 0;
  const canSubmit = !submitting && !loopErr && nameOk
    && (preset !== 'Once' || at.trim().length > 0)
    && (preset !== 'Custom' || cron.trim().length > 0);

  const toggleWeekday = (k) => setWeekdays((p) => (p.includes(k) ? p.filter((d) => d !== k) : [...p, k]));
  const setPromptAt = (i, v) => setPrompts((p) => p.map((x, idx) => (idx === i ? v : x)));
  const addPrompt = () => setPrompts((p) => [...p, '']);
  const removePrompt = (i) => setPrompts((p) => (p.length <= 1 ? [''] : p.filter((_, idx) => idx !== i)));
  const clearAll = () => setPrompts(['']);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const split = splitPrompts(text, separator);
    setPrompts(split.length ? split : ['']);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const body = buildLoopBody({
        mode: hasObjective ? 'REPEAT_UNTIL' : 'ONCE',
        prompts: cleanPrompts, separator, objective, rating, confidence, maxTurns,
      });
      body.name = name.trim();                 // mandatory display name
      if (!hasObjective) delete body.gate;     // single pass, no gate
      if (cadence) body.cadence = cadence;     // a clock starts it; else fires now
      await onSave(body);
    } catch (err) {
      setError(err?.message || 'Failed to save schedule');
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          role="dialog" aria-modal="true"
        >
          <motion.div
            className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            initial={{ scale: 0.98, y: 6 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.98, y: 6 }}
          >
            <header className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h3 className="text-base font-semibold text-gray-900">
                {isEdit ? 'Edit schedule' : 'New schedule'}
              </h3>
              <button type="button" onClick={onClose} className="text-sm text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
            </header>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              {/* Name — mandatory; the list shows THIS, not the prompt. */}
              <div className="border-b border-gray-100 px-5 py-3">
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
                  Name
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Nightly novel build"
                    maxLength={120}
                    className={`mt-0.5 rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 ${
                      nameOk
                        ? 'border-gray-300 focus:border-blue-400 focus:ring-blue-400'
                        : 'border-amber-300 focus:border-amber-400 focus:ring-amber-400'
                    }`}
                    data-testid="schedule-name-input"
                  />
                  {!nameOk ? (
                    <span className="text-[11px] font-normal text-amber-600">
                      A name is required — it&apos;s what shows in the schedule list.
                    </span>
                  ) : null}
                </label>
              </div>
              <div className="flex min-h-0 flex-1">
                {/* WHEN it starts — left rail (label + tight description), like "Add skills" */}
                <div className="w-56 shrink-0 overflow-y-auto border-r border-gray-100 py-2">
                  <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">When</p>
                  {PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => setPreset(p)}
                      data-testid={`schedule-preset-${p}`}
                      className={`flex w-full flex-col gap-0.5 border-l-2 px-3 py-1.5 text-left transition-colors ${
                        preset === p
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-transparent hover:bg-gray-50'
                      }`}>
                      <span className={`text-xs font-medium ${preset === p ? 'text-blue-700' : 'text-gray-800'}`}>{p}</span>
                      <span className="text-[10px] leading-tight text-gray-400">{PRESET_DESC[p]}</span>
                    </button>
                  ))}
                </div>

                {/* Right column — per-preset config + prompts + bounds + objective */}
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                {['Daily', 'Weekdays', 'Weekly', 'Monthly'].includes(preset) ? (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Time</label>
                    <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm" />
                  </div>
                ) : null}
                {preset === 'Weekly' ? (
                  <div className="flex flex-wrap gap-1.5">
                    {WEEKDAYS.map((d) => (
                      <button key={d.key} type="button" onClick={() => toggleWeekday(d.key)}
                        className={`${CHIP} ${weekdays.includes(d.key) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {d.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {preset === 'Monthly' ? (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Day of month</label>
                    <input type="number" min="1" max="28" value={dayOfMonth}
                      onChange={(e) => setDayOfMonth(e.target.value)}
                      className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm" />
                  </div>
                ) : null}
                {preset === 'Once' ? (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">At</label>
                    <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm" />
                  </div>
                ) : null}
                {preset === 'Custom' ? (
                  <div>
                    <label className="mb-1 block text-xs text-gray-600">Cron (6 fields)</label>
                    <input type="text" value={cron} onChange={(e) => setCron(e.target.value)}
                      placeholder="0 9 ? * MON-FRI *"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 font-mono text-sm" />
                  </div>
                ) : null}
                {estFires > 0 ? (
                  <p className="text-[11px] text-gray-500">≈ {estFires} start{estFires === 1 ? '' : 's'} / month.</p>
                ) : null}

                {/* Prompts — bordered box: a scrollable list (caps the height so
                    100s of prompts don't blow up the form) + a sticky footer with
                    Add / Upload / Separator / Clear. */}
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-gray-700">Episodic prompts</label>
                      <span className="text-[10px] text-gray-400" data-testid="schedule-prompt-count">
                        {prompts.length} {prompts.length === 1 ? 'episode' : 'episodes'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[10px] leading-tight text-gray-400">
                      Break the work into ordered steps — the room runs them top to
                      bottom, each building on the last. One full pass = one turn.
                    </p>
                  </div>
                  {/* ~3 rows tall, then scrolls. */}
                  <div className="max-h-44 space-y-1.5 overflow-y-auto p-2" data-testid="schedule-prompt-list">
                    {prompts.map((p, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        <textarea value={p} onChange={(e) => setPromptAt(i, e.target.value)} rows={2}
                          placeholder={`Episode ${i + 1}`}
                          className="w-full resize-y rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          data-testid={`schedule-prompt-${i}`} />
                        <button type="button" onClick={() => removePrompt(i)}
                          className="mt-1 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-red-600"
                          title="Remove prompt">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* Footer: actions live here, out of the scroll area. */}
                  <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-3 py-1.5">
                    <button type="button" onClick={addPrompt}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
                      data-testid="schedule-add-prompt">
                      <Plus className="h-3 w-3" /> Add episode
                    </button>
                    <div className="flex items-center gap-3">
                      <label
                        className="flex items-center gap-1 text-[11px] text-gray-500"
                        title="Upload a .txt/.md text file; prompts are split on this delimiter. Default is a blank line (\n\n); use e.g. | for single-line prompts. PDFs/Office files aren't supported.">
                        Upload .txt/.md, split on
                        <input type="text" value={separator === '\n\n' ? '\\n\\n' : separator}
                          onChange={(e) => setSeparator(e.target.value === '\\n\\n' ? '\n\n' : e.target.value)}
                          className="w-14 rounded border border-gray-300 px-1 py-0.5 font-mono text-[11px]"
                          data-testid="schedule-separator" />
                      </label>
                      <button type="button" onClick={() => fileRef.current?.click()}
                        className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                        data-testid="schedule-upload">
                        <Upload className="h-3 w-3" /> Upload
                      </button>
                      <button type="button" onClick={clearAll}
                        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-600"
                        data-testid="schedule-clear-all">
                        <X className="h-3 w-3" /> Clear all
                      </button>
                      <input ref={fileRef} type="file" accept=".txt,.md,text/plain"
                        onChange={onFile} className="hidden" data-testid="schedule-file" />
                    </div>
                  </div>
                </div>

                {/* Max turns — how many full passes (turns) the schedule runs. */}
                <div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-700">Max turns</label>
                    <input type="number" min="1" value={maxTurns} onChange={(e) => setMaxTurns(e.target.value)}
                      className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      data-testid="schedule-max-turns" />
                  </div>
                  <p className="mt-0.5 text-[11px] leading-tight text-gray-400">
                    How many times to run the whole episode list. One run-through of
                    all episodes = one turn.
                    {hasObjective
                      ? ' The objective below can stop earlier; this is the hard cap so it never loops forever.'
                      : ' With no objective, it simply runs this many times.'}
                  </p>
                </div>

                {/* WHEN it stops — Objective gate (rating + confidence alongside) */}
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      Objective <span className="font-normal text-gray-400">— optional</span>
                    </label>
                    <p className="mb-1.5 text-[11px] leading-tight text-gray-500">
                      The goal each turn is judged against. After every full turn an
                      independent <span className="font-medium text-gray-700">judge</span> reviews
                      the result and scores it. It keeps running turns — feeding its
                      feedback into the next — until the score clears your targets, or
                      Max turns is reached. Leave blank for a plain run with no judging.
                    </p>
                    <textarea value={objective} onChange={(e) => setObjective(e.target.value)} rows={2}
                      placeholder="e.g. A working REST endpoint with passing tests and error handling."
                      className="w-full resize-y rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      data-testid="schedule-objective" />
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600">
                      Rating ≥
                      <select value={rating} onChange={(e) => setRating(e.target.value)}
                        className="rounded-md border border-gray-300 px-1.5 py-1 text-sm"
                        data-testid="schedule-rating">
                        {ratingOptions().map((r) => <option key={r} value={r}>{r}</option>)}
                        <option value="10" disabled>10 (n/a)</option>
                      </select>
                      <span className="text-gray-400">/ 10</span>
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600">
                      Confidence ≥
                      <input type="number" min="0.05" max="1" step="0.05" value={confidence}
                        onChange={(e) => setConfidence(e.target.value)}
                        className="w-20 rounded-md border border-gray-300 px-1.5 py-1 text-sm"
                        data-testid="schedule-confidence" />
                    </label>
                  </div>
                  <p className="text-[11px] leading-tight text-gray-400">
                    Stop once the judge rates the result at least this high
                    <span className="text-gray-500"> and</span> is at least this sure
                    of its rating.
                  </p>
                </div>

                {error ? (
                  <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
                ) : null}
                </div>{/* right column */}
              </div>{/* flex row */}

              <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
                <button type="button" onClick={onClose} disabled={submitting}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={!canSubmit}
                  className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="schedule-save">
                  {submitting ? 'Saving…' : (isEdit ? 'Save' : (isNow ? 'Start' : 'Schedule'))}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Local mirror of the API cadence → expr (so the estimate works pre-submit).
function buildExpr(cadence) {
  const t = String(cadence.type || '').toUpperCase();
  if (t === 'RATE') {
    const v = cadence.value; const u = v === 1 ? cadence.unit.replace(/s$/, '') : cadence.unit;
    return `rate(${v} ${u})`;
  }
  if (t === 'ONCE') return `at(${cadence.at || ''})`;
  if (cadence.expr) return `cron(${cadence.expr})`;
  const dom = cadence.dayOfMonth || '?';
  const dow = cadence.dayOfWeek || (dom !== '?' ? '?' : '*');
  return `cron(${cadence.minute || '0'} ${cadence.hour || '0'} ${dom} ${cadence.month || '*'} ${dow} *)`;
}
